import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, IconButton, Link, Popover, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactElement, ReactNode, TouchEvent as ReactTouchEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

export type HelpPopoverTriggerVariant = 'icon' | 'outlinedButton';

export interface HelpPopoverProps {
  id: string;
  text: ReactNode;
  ariaLabel?: string;
  /** Defaults to a compact info icon; use `outlinedButton` for a labeled help trigger. */
  triggerVariant?: HelpPopoverTriggerVariant;
  /** Label for `outlinedButton` triggers; falls back to `ariaLabel` when omitted. */
  triggerLabel?: string;
  children?: ReactNode;
}

const normalizeMarkdown = (s: string): string => (s ?? '').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

const codeFontStyles = (theme: Theme) => ({
  fontSize: (theme.typography as { code?: { fontSize?: string } }).code?.fontSize ?? theme.typography.body2.fontSize,
  fontFamily: (theme.typography as { code?: { fontFamily?: string } }).code?.fontFamily ?? theme.typography.fontFamily,
});

type CustomTriggerProps = {
  'aria-describedby'?: string;
  'aria-haspopup'?: 'dialog';
  onMouseEnter?: (event: ReactMouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: ReactMouseEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
};

const composeHandlers =
  <EventType,>(first: ((event: EventType) => void) | undefined, second: (event: EventType) => void) =>
  (event: EventType) => {
    first?.(event);
    second(event);
  };

const HelpPopover = ({ id, text, ariaLabel = 'more information', triggerVariant = 'icon', triggerLabel, children }: HelpPopoverProps) => {
  const theme = useTheme();
  const triggerRef = useRef<HTMLElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const isMarkdown = typeof text === 'string';

  const containsPopoverElement = useCallback((target: EventTarget | null) => {
    if (!target) return false;
    if (!(target instanceof Node)) return false;
    const node = target as Node;
    return Boolean(triggerRef.current?.contains(node) || popoverContentRef.current?.contains(node));
  }, []);

  const handlePopoverOpen = useCallback((event: ReactMouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handlePointerOpen = useCallback((event: ReactMouseEvent<HTMLElement> | ReactTouchEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleTriggerMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (containsPopoverElement(event.relatedTarget)) return;
      handlePopoverClose();
    },
    [containsPopoverElement, handlePopoverClose]
  );

  const handleTriggerBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (containsPopoverElement(event.relatedTarget)) return;
      handlePopoverClose();
    },
    [containsPopoverElement, handlePopoverClose]
  );

  const handlePopoverMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (containsPopoverElement(event.relatedTarget)) return;
      handlePopoverClose();
    },
    [containsPopoverElement, handlePopoverClose]
  );

  const handlePopoverBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (containsPopoverElement(event.relatedTarget)) return;
      handlePopoverClose();
    },
    [containsPopoverElement, handlePopoverClose]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        handlePopoverClose();
      }
    },
    [handlePopoverClose]
  );

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: globalThis.MouseEvent | globalThis.TouchEvent) => {
      if (containsPopoverElement(event.target)) return;
      handlePopoverClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [containsPopoverElement, handlePopoverClose, open]);

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <Link href={href ?? '#'} target="_blank" rel="noopener noreferrer" variant="body2">
          {children}
        </Link>
      ),
      p: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body2" component="div" sx={{ display: 'block', margin: '0 0 0.5em 0', '&:last-child': { marginBottom: 0 } }}>
          {children}
        </Typography>
      ),
      strong: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body2" component="strong" fontWeight={600}>
          {children}
        </Typography>
      ),
      em: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body2" component="em" fontStyle="italic">
          {children}
        </Typography>
      ),
      h1: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="subtitle1" component="div" fontWeight={600} sx={{ display: 'block', mt: 0.5, mb: 0.25 }}>
          {children}
        </Typography>
      ),
      h2: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="subtitle2" component="div" fontWeight={600} sx={{ display: 'block', mt: 0.5, mb: 0.25 }}>
          {children}
        </Typography>
      ),
      h3: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body1" component="div" fontWeight={600} sx={{ display: 'block', mt: 0.5, mb: 0.25 }}>
          {children}
        </Typography>
      ),
      ul: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ul" sx={{ margin: '0.25em 0', paddingLeft: 2 }}>
          {children}
        </Box>
      ),
      ol: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ol" sx={{ margin: '0.25em 0', paddingLeft: 2 }}>
          {children}
        </Box>
      ),
      li: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body2" component="li" sx={{ display: 'list-item', marginBottom: 0.25 }}>
          {children}
        </Typography>
      ),
      img: ({ src, alt }: { src?: string; alt?: string }) => (
        <Box component="img" src={src} alt={alt ?? ''} sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 0.5 }} />
      ),
      code: ({ inline, children }: { inline?: boolean; children?: React.ReactNode }) =>
        inline ? (
          <Box
            component="code"
            sx={{
              ...codeFontStyles(theme),
              backgroundColor: 'action.hover',
              px: 0.5,
              borderRadius: 0.5,
              lineHeight: 'inherit',
              display: 'inline',
            }}
          >
            {children}
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              backgroundColor: 'action.hover',
              p: 1,
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
              margin: '0.5em 0',
            }}
          >
            <Box component="code" sx={codeFontStyles(theme)}>
              {children}
            </Box>
          </Box>
        ),
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <Box component="blockquote" sx={{ borderLeft: 2, borderColor: 'divider', pl: 1, my: 0.5, marginLeft: 0 }}>
          {children}
        </Box>
      ),
      hr: () => <Box component="hr" sx={{ border: 'none', borderTop: 1, borderColor: 'divider', my: 0.5 }} />,
    }),
    [theme]
  );

  const describedBy = open ? id : undefined;
  const triggerAriaLabel = triggerVariant === 'outlinedButton' ? triggerLabel ?? ariaLabel : ariaLabel;
  const hasCustomTriggerElement = isValidElement<CustomTriggerProps>(children);
  const triggerEventProps = {
    'aria-describedby': describedBy,
    'aria-haspopup': 'dialog' as const,
    onMouseEnter: handlePopoverOpen,
    onMouseLeave: handleTriggerMouseLeave,
    onMouseDown: handlePointerOpen,
    onClick: handlePopoverOpen,
    onTouchStart: handlePointerOpen,
    onFocus: handlePopoverOpen,
    onBlur: handleTriggerBlur,
    onKeyDown: handleKeyDown,
  };
  const customTriggerEventProps: CustomTriggerProps = {
    'aria-describedby': describedBy,
    'aria-haspopup': 'dialog',
    onMouseEnter: handlePopoverOpen,
    onMouseLeave: handleTriggerMouseLeave,
    onFocus: handlePopoverOpen,
    onBlur: handleTriggerBlur,
    onKeyDown: handleKeyDown,
  };

  const customTrigger = hasCustomTriggerElement
    ? cloneElement(children as ReactElement<CustomTriggerProps>, {
        'aria-describedby': describedBy,
        'aria-haspopup': 'dialog',
        onMouseEnter: composeHandlers(children.props.onMouseEnter, customTriggerEventProps.onMouseEnter!),
        onMouseLeave: composeHandlers(children.props.onMouseLeave, customTriggerEventProps.onMouseLeave!),
        onFocus: composeHandlers(children.props.onFocus, customTriggerEventProps.onFocus!),
        onBlur: composeHandlers(children.props.onBlur, customTriggerEventProps.onBlur!),
        onKeyDown: composeHandlers(children.props.onKeyDown, customTriggerEventProps.onKeyDown!),
      })
    : children;

  return (
    <>
      {children ? (
        <Box
          component="span"
          ref={(node: HTMLElement | null) => {
            triggerRef.current = node;
          }}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handleTriggerMouseLeave}
          onFocus={hasCustomTriggerElement ? undefined : handlePopoverOpen}
          onBlur={hasCustomTriggerElement ? undefined : handleTriggerBlur}
          onKeyDown={hasCustomTriggerElement ? undefined : handleKeyDown}
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {customTrigger}
        </Box>
      ) : triggerVariant === 'outlinedButton' ? (
        <Button
          ref={(node: HTMLButtonElement | null) => {
            triggerRef.current = node;
          }}
          variant="outlined"
          size="small"
          aria-label={triggerAriaLabel}
          endIcon={<InfoOutlinedIcon sx={{ fontSize: '1rem' }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            py: 0.5,
            px: 1,
            minWidth: 0,
            bgcolor: 'accent.opaque',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'accent.opaque',
              borderColor: 'divider',
            },
          }}
          {...triggerEventProps}
        >
          {triggerLabel ?? ariaLabel}
        </Button>
      ) : (
        <IconButton
          ref={(node: HTMLButtonElement | null) => {
            triggerRef.current = node;
          }}
          size="small"
          aria-label={triggerAriaLabel}
          sx={{ p: 0.25, color: 'inherit' }}
          {...triggerEventProps}
        >
          <InfoIcon fontSize="inherit" />
        </IconButton>
      )}
      <Popover
        id={id}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: { maxWidth: 320, pointerEvents: 'auto' },
          },
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: !children && triggerVariant === 'outlinedButton' ? 'center' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: !children && triggerVariant === 'outlinedButton' ? 'center' : 'left',
        }}
        onClose={handlePopoverClose}
        disableAutoFocus
        disableRestoreFocus
      >
        <Box
          ref={popoverContentRef}
          onMouseLeave={handlePopoverMouseLeave}
          onBlur={handlePopoverBlur}
          sx={{
            p: 1,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            '& a': { color: 'primary.main' },
          }}
        >
          {isMarkdown ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} components={markdownComponents}>
              {normalizeMarkdown(text as string)}
            </ReactMarkdown>
          ) : (
            <Typography variant="body2">{text}</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default HelpPopover;
